import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import {
  ACTION_LIST,
  OUTCOME_LIST,
  canBuff,
  canCounterKick,
  hasOutcome,
  isOutcomeValid,
  normalizeCell,
  type ScriptCell,
} from '../dev/battleScript';
import { makeImageButton } from './Button';
import { OUTCOME_COLOR, type ScriptIcons } from './ScriptCell';

const PANEL_W_FRAC = 0.82;
const PANEL_H_FRAC = 0.92;
const PANEL_COLOR = 0x2a2018;
const PANEL_BORDER = 0xb38a4a;
const LABEL_COLOR = 0xd8c39a;
const SELECT_BORDER = 0xffd000;

const ACTION_BTN = 52;
const OUTCOME_BTN_W = 64;
const OUTCOME_BTN_H = 26;
const ROW_GAP = 10;

export type ActionPaletteOptions = {
  cell: ScriptCell;
  icons: ScriptIcons;
  selectBtn: Texture;
  // Size of the area the palette covers (the modal panel inner size).
  width: number;
  height: number;
  fontFamily?: string;
  textResolution: number;
  onChange: (cell: ScriptCell) => void;
  onClose: () => void;
};

// Popup picker bound to one cell. Edits a local copy and emits onChange live so
// the underlying ScriptCellView re-renders; onClose on tap-outside / Done.
export class ActionPalette extends Container {
  private cell: ScriptCell;
  private readonly icons: ScriptIcons;
  private readonly selectBtn: Texture;
  private readonly fontFamily?: string;
  private readonly textResolution: number;
  private readonly onChange: (cell: ScriptCell) => void;
  private readonly onClose: () => void;
  private readonly panel = new Container();
  private readonly content = new Container();
  private readonly panelW: number;
  private readonly panelH: number;

  constructor(opts: ActionPaletteOptions) {
    super();
    this.cell = opts.cell;
    this.icons = opts.icons;
    this.selectBtn = opts.selectBtn;
    this.fontFamily = opts.fontFamily;
    this.textResolution = opts.textResolution;
    this.onChange = opts.onChange;
    this.onClose = opts.onClose;
    this.panelW = opts.width * PANEL_W_FRAC;
    this.panelH = opts.height * PANEL_H_FRAC;

    // Tap-outside scrim (taps on the panel don't reach it - sibling, no bubble).
    const scrim = new Graphics().rect(0, 0, opts.width, opts.height).fill({ color: 0x000000, alpha: 0.45 });
    scrim.eventMode = 'static';
    scrim.on('pointertap', () => this.onClose());
    this.addChild(scrim);

    this.panel.position.set((opts.width - this.panelW) / 2, (opts.height - this.panelH) / 2);
    const bg = new Graphics()
      .roundRect(0, 0, this.panelW, this.panelH, 10)
      .fill(PANEL_COLOR)
      .stroke({ color: PANEL_BORDER, width: 2 });
    bg.eventMode = 'static'; // swallow taps so they don't close the palette
    this.panel.addChild(bg);
    this.panel.addChild(this.content);
    this.addChild(this.panel);

    this.rebuild();
  }

  private edit(patch: Partial<ScriptCell>): void {
    this.cell = normalizeCell({ ...this.cell, ...patch });
    this.onChange(this.cell);
    this.rebuild();
  }

  private rebuild(): void {
    this.content.removeChildren().forEach((c) => c.destroy());
    let y = 14;
    const cx = this.panelW / 2;

    y = this.addLabel('ACTION', cx, y) + 4;
    y = this.addActionRow(cx, y) + ROW_GAP;

    if (canBuff(this.cell.action)) {
      y = this.addToggle('CHARGE BUFF', this.cell.buff, false, cx, y, () =>
        this.edit({ buff: !this.cell.buff }),
      ) + ROW_GAP;
    }

    if (hasOutcome(this.cell.action)) {
      y = this.addLabel('OUTCOME', cx, y) + 4;
      y = this.addOutcomeRows(cx, y) + ROW_GAP;
      const counterEnabled = canCounterKick(this.cell.outcome);
      // last row in the flow - no need to advance y past it
      this.addToggle('COUNTER = KICK', this.cell.counterKick, !counterEnabled, cx, y, () => {
        if (counterEnabled) this.edit({ counterKick: !this.cell.counterKick });
      });
    }

    this.addDone(cx, this.panelH - 44);
  }

  private addLabel(text: string, cx: number, y: number): number {
    const t = new Text({
      text,
      style: { fontFamily: this.fontFamily, fontSize: 12, fontWeight: 'bold', fill: LABEL_COLOR, letterSpacing: 1 },
      resolution: this.textResolution,
    });
    t.anchor.set(0.5, 0);
    t.position.set(cx, y);
    this.content.addChild(t);
    return y + 16;
  }

  private addActionRow(cx: number, y: number): number {
    const n = ACTION_LIST.length;
    const gap = 8;
    const totalW = n * ACTION_BTN + (n - 1) * gap;
    let x = cx - totalW / 2;
    for (const { action, label } of ACTION_LIST) {
      const selected = this.cell.action === action;
      const btn = this.iconButton(this.icons[action], label, selected, () =>
        this.edit({ action }),
      );
      btn.position.set(x, y);
      this.content.addChild(btn);
      x += ACTION_BTN + gap;
    }
    return y + ACTION_BTN + 12;
  }

  private addOutcomeRows(cx: number, y: number): number {
    // 6 outcomes in two rows of 3.
    const perRow = 3;
    const gap = 8;
    const rowW = perRow * OUTCOME_BTN_W + (perRow - 1) * gap;
    OUTCOME_LIST.forEach(({ outcome, label }, i) => {
      const row = Math.floor(i / perRow);
      const col = i % perRow;
      const x = cx - rowW / 2 + col * (OUTCOME_BTN_W + gap);
      const yy = y + row * (OUTCOME_BTN_H + gap);
      const enabled = isOutcomeValid(this.cell.action, outcome);
      const selected = this.cell.outcome === outcome;
      const btn = this.badgeButton(label, OUTCOME_COLOR[outcome], selected, !enabled, () => {
        if (enabled) this.edit({ outcome });
      });
      btn.position.set(x, yy);
      this.content.addChild(btn);
    });
    return y + 2 * OUTCOME_BTN_H + gap;
  }

  private iconButton(icon: Texture | undefined, label: string, selected: boolean, onTap: () => void): Container {
    const c = new Container();
    const plate = new Graphics()
      .roundRect(0, 0, ACTION_BTN, ACTION_BTN, 8)
      .fill({ color: 0x000000, alpha: 0.25 })
      .stroke({ color: selected ? SELECT_BORDER : PANEL_BORDER, width: selected ? 3 : 1 });
    c.addChild(plate);
    if (icon) {
      const s = new Sprite(icon);
      const target = ACTION_BTN * 0.66;
      s.anchor.set(0.5);
      s.scale.set(target / Math.max(icon.width, icon.height));
      s.position.set(ACTION_BTN / 2, ACTION_BTN * 0.42);
      c.addChild(s);
    }
    const t = new Text({
      text: label,
      style: { fontFamily: this.fontFamily, fontSize: 9, fill: LABEL_COLOR },
      resolution: this.textResolution,
    });
    t.anchor.set(0.5, 1);
    t.position.set(ACTION_BTN / 2, ACTION_BTN - 3);
    c.addChild(t);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointertap', onTap);
    return c;
  }

  private badgeButton(
    label: string,
    color: number,
    selected: boolean,
    disabled: boolean,
    onTap: () => void,
  ): Container {
    const c = new Container();
    const plate = new Graphics()
      .roundRect(0, 0, OUTCOME_BTN_W, OUTCOME_BTN_H, 6)
      .fill({ color: 0x000000, alpha: 0.3 })
      .stroke({ color: selected ? SELECT_BORDER : PANEL_BORDER, width: selected ? 3 : 1 });
    c.addChild(plate);
    const t = new Text({
      text: label,
      style: { fontFamily: this.fontFamily, fontSize: 11, fontWeight: 'bold', fill: color },
      resolution: this.textResolution,
    });
    t.anchor.set(0.5);
    t.position.set(OUTCOME_BTN_W / 2, OUTCOME_BTN_H / 2);
    c.addChild(t);
    c.alpha = disabled ? 0.3 : 1;
    if (!disabled) {
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.on('pointertap', onTap);
    }
    return c;
  }

  private addToggle(
    label: string,
    on: boolean,
    disabled: boolean,
    cx: number,
    y: number,
    onTap: () => void,
  ): number {
    const w = 150;
    const h = 26;
    const c = new Container();
    const plate = new Graphics()
      .roundRect(0, 0, w, h, 6)
      .fill(on ? { color: SELECT_BORDER, alpha: 0.85 } : { color: 0x000000, alpha: 0.3 })
      .stroke({ color: PANEL_BORDER, width: 1 });
    c.addChild(plate);
    const t = new Text({
      text: `${label}: ${on ? 'ON' : 'OFF'}`,
      style: { fontFamily: this.fontFamily, fontSize: 11, fontWeight: 'bold', fill: on ? 0x2a2018 : LABEL_COLOR },
      resolution: this.textResolution,
    });
    t.anchor.set(0.5);
    t.position.set(w / 2, h / 2);
    c.addChild(t);
    c.position.set(cx - w / 2, y);
    c.alpha = disabled ? 0.35 : 1;
    if (!disabled) {
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.on('pointertap', onTap);
    }
    this.content.addChild(c);
    return y + h;
  }

  private addDone(cx: number, cy: number): void {
    const btn = makeImageButton({
      texture: this.selectBtn,
      label: 'DONE',
      height: 30,
      fontFamily: this.fontFamily,
      fontSize: 13,
      textColor: 0x000000,
      textResolution: this.textResolution,
      letterSpacing: 2,
      onTap: () => this.onClose(),
    });
    btn.position.set(cx, cy);
    this.content.addChild(btn);
  }
}
